import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, CardBody, Table, Button, Input, Spinner, Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { productActions } from '../../sagas/productSlice';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaPrint, FaSearch } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import JsBarcode from 'jsbarcode';
import jsPDF from 'jspdf';
import { debounce } from 'lodash';

const BarcodePage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [selectedProducts, setSelectedProducts] = useState({}); // { productId: quantity }
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [criteria, setSearchCriteria] = useState({ query: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);

  const {
    products,
    loading: productsLoading,
    totalPages,
    totalElements,
    pageNumber,
    pageSize,
    error,
  } = useSelector((state) => state.products);
  const searchInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Auto focus on mount
    searchInputRef.current?.focus();

    // Add keyboard shortcut
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Update product fetch effect
  useEffect(() => {
    dispatch(productActions.search({
      criteria,
      page: currentPage,
      size: 10,
      onSuccess: () => setIsSearching(false)
    }));
  }, [dispatch, currentPage, pageSize, criteria]); // Remove selectedProducts from here

  useEffect(() => {
    const savedSelections = localStorage.getItem('selectedBarcodeProducts');
    if (savedSelections) {
      setSelectedProducts(JSON.parse(savedSelections));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('selectedBarcodeProducts', JSON.stringify(selectedProducts));
  }, [selectedProducts]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      setIsSearching(true);
      dispatch(productActions.search({
        criteria: { query },
        page: 1,
        size: 8,
        onSuccess: () => setIsSearching(false)
      }));
    }, 500), // Wait 500ms after last keystroke
    []
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    dispatch(productActions.search({
      criteria,
      page: page - 1,
      size: pageSize,
      onSuccess: () => setIsSearching(false)
    }));
  }, [dispatch, criteria, pageSize, selectedProducts]); // Add selectedProducts to dependencies



  const handleSelectAll = (e) => {
    setSelectAll(e.target.checked);
    if (e.target.checked) {
      setSelectedProducts(products.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (product, quantity = 1) => {
    setSelectedProducts(prev => {
      if (prev[product.id]) {
        const { [product.id]: removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [product.id]: {
          product,
          quantity
        }
      };
    });
  };

  // Update quantity handler
  const handleQuantityChange = (productId, quantity) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        quantity: parseInt(quantity) || 1
      }
    }));
  };

  const generateBarcodes = async () => {
    if (Object.keys(selectedProducts).length === 0) return;
  
    setLoading(true);
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        margins: {
          top: 5,
          left: 8,
          right: 8,
          bottom: 5
        }
      });
  
      // Fixed margins and layout settings
      const settings = {
        marginTop: 5,      // Fixed top margin 5mm
        marginLeft: 8,     // Fixed left margin 8mm
        barcodeWidth: 45,  // Adjusted for 4 columns
        barcodeHeight: 17,
        textHeight: 3,
        colsPerPage: 4,
        spacingX: 2,
        spacingY: 3
      };
  
      const totalHeight = settings.barcodeHeight + settings.textHeight;
      let currentX = settings.marginLeft;
      let currentY = settings.marginTop;
      let itemsInPage = 0;
  
      Object.entries(selectedProducts).forEach(([productId, { product, quantity }]) => {
        for (let i = 0; i < quantity; i++) {
          if (itemsInPage > 0 && itemsInPage % (settings.colsPerPage * 15) === 0) {
            doc.addPage();
            currentX = settings.marginLeft;
            currentY = settings.marginTop;
            itemsInPage = 0;
          }
  
          // Create barcode
          const canvas = document.createElement('canvas');
          canvas.style.quality = 'best';
  
          JsBarcode(canvas, product.code || product.id.toString(), {
            format: "CODE128",
            width: 2,
            height: 40,
            displayValue: true,
            fontSize: 10,
            textMargin: 4,
            margin: 4,
            background: '#FFFFFF',
            lineColor: '#000000',
            font: 'arial'
          });
  
          // Add barcode to PDF
          const imageData = canvas.toDataURL('image/jpeg', 1.0);
          doc.addImage(imageData, 'JPEG', currentX, currentY, settings.barcodeWidth, settings.barcodeHeight);
  
          // Update position
          itemsInPage++;
          if (itemsInPage % settings.colsPerPage === 0) {
            currentX = settings.marginLeft;
            currentY += totalHeight + settings.spacingY;
          } else {
            currentX += settings.barcodeWidth + settings.spacingX;
          }
        }
      });
  
      doc.save('barcodes.pdf');
    } catch (error) {
      console.error('Error generating barcodes:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <Card className="shadow-sm">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">
                  <FaBarcode className="me-2" />
                  {t('barcode.title')}
                </h4>
                <Button
                  color="primary"
                  onClick={generateBarcodes}
                  disabled={selectedProducts.length === 0 || loading}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <FaPrint className="me-1" />
                      {t('barcode.generate')}
                    </>
                  )}
                </Button>
              </div>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </div>
                  <div className="mt-2 text-muted">
                    {t('common.loadingData')}
                  </div>
                </div>
              ) : (<>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  {/* Search Input - Left Side */}
                  <div className="position-relative flex-grow-1 me-3">
                    <Input
                      innerRef={searchInputRef}
                      type="text"
                      placeholder={t('product.searchFocusHint')}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className={`ps-4 pe-5 ${isSearching ? 'searching' : ''}`}
                      title={t('product.searchShortcut')}
                    />
                    <FaSearch
                      style={{
                        position: 'absolute',
                        left: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#adb5bd'
                      }}
                      size={14}
                    />
                    <FaBarcode
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#adb5bd'
                      }}
                      size={20}
                      title={t('product.scanHint')}
                    />
                  </div>
                </div>
                <div className="table-responsive">
                  <Table responsive bordered hover>
                    <thead>
                      <tr>
                        <th width="50">
                          <Input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>{t('product.code')}</th>
                        <th>{t('product.description')}</th>
                        <th>{t('product.barcode')}</th>
                        <th width="100">{t('product.quantity')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productsLoading ? (
                        <tr>
                          <td colSpan="4" className="text-center py-4">
                            <Spinner color="primary" />
                          </td>
                        </tr>
                      ) : !products || products.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center py-4">
                            {t('product.noProducts')}
                          </td>
                        </tr>
                      ) : (
                        products.map(product => (
                          <tr key={product.id}>
                            <td>
                              <Input
                                type="checkbox"
                                checked={selectedProducts[product.id] !== undefined}
                                onChange={() => handleSelectProduct(product)}
                              />
                            </td>
                            <td>{product.barcode}</td>
                            <td>{product.description}</td>
                            <td>{product.barcode || 'Non spécifié'}</td>
                            <td width="100">
                              {selectedProducts[product.id] !== undefined && (
                                <Input
                                  type="number"
                                  min="1"
                                  value={selectedProducts[product.id].quantity}
                                  onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                                  className="form-control-sm"
                                />
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <small className="text-muted">
                    {t('product.showing', {
                      start: (currentPage - 1) * pageSize + 1,
                      end: Math.min(currentPage * pageSize, totalElements),
                      total: totalElements,
                      currentPage: currentPage,
                      totalPages: totalPages
                    })}
                  </small>
                  <Pagination size="sm" className="mb-0">
                    <PaginationItem disabled={currentPage === 1}>
                      <PaginationLink first onClick={() => handlePageChange(1)}>
                        <FaAngleDoubleLeft size={12} />
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={currentPage === 1}>
                      <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)}>
                        <FaAngleLeft size={12} />
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={currentPage === totalPages}>
                      <PaginationLink next onClick={() => handlePageChange(currentPage + 1)}>
                        <FaAngleRight size={12} />
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={currentPage === totalPages}>
                      <PaginationLink last onClick={() => handlePageChange(totalPages)}>
                        <FaAngleDoubleRight size={12} />
                      </PaginationLink>
                    </PaginationItem>
                  </Pagination>
                </div>
              </>)}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BarcodePage;